import { RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { LivingLogoIcon } from "@/components/brand/LivingLogoIcon";
import { LivingLogoLED } from "@/components/brand/LivingLogoLED";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Heading, Text } from "@/components/ui/typography";
import { clearTokenCache, useChatKitToken } from "@/features/assistant/hooks/useChatKitToken";
import { cn } from "@/lib/utils";
import { ChatKit, useChatKit } from "@openai/chatkit-react";
import { semanticTokens } from "@packages/config/theme";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useIsDarkTheme } from "../providers/ThemeProvider";

/**
 * PublicChatKitButton - Floating Action Button for public pages
 * Uses OpenAI ChatKit React SDK directly
 * 
 * NOTE: @openai/chatkit-react handles its own loading/initialization.
 * We only need to provide getClientSecret for authentication.
 */
export function PublicChatKitButton() {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const isDarkTheme = useIsDarkTheme();
  const isMountedRef = useRef(true);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Detect mobile for bottom sheet on mobile, side panel on desktop
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Use the token management hook
  const { getClientSecret } = useChatKitToken({
    path: location.pathname,
    isPublic: true,
    onTokenFetched: () => {
      if (isMountedRef.current) {
        setError(null);
      }
    },
    onTokenError: (err) => {
      if (isMountedRef.current) {
        setError(err.message);
      }
    },
  });

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    setPortalNode(typeof document !== 'undefined' ? document.body : null);
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Focus trap: handle Escape key to close
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  // Focus the dialog when opened (accessibility)
  useEffect(() => {
    if (open && dialogRef.current) {
      const focusable = dialogRef.current.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      focusable?.focus();
    }
  }, [open]);

  // Memoize ChatKit config to prevent unnecessary re-renders
  const chatKitConfig = useMemo(() => ({
    api: { getClientSecret },
    locale: (typeof navigator !== 'undefined' && navigator.language
      ? navigator.language
      : 'en') as 'en' | 'ru' | 'es' | 'fr' | 'de' | 'it' | 'pt' | 'ja' | 'ko' | 'zh' | undefined,
    theme: 'light' as const,
    startScreen: {
      greeting: t("chatKit.greeting"),
      prompts: [
        { label: t("chatKit.promptLabels.howItWorks"), prompt: t("chatKit.prompts.howItWorks") },
        { label: t("chatKit.promptLabels.supportedCrypto"), prompt: t("chatKit.prompts.supportedCrypto") },
        { label: t("chatKit.promptLabels.kycVerification"), prompt: t("chatKit.prompts.kycVerification") },
        { label: t("chatKit.promptLabels.feesLimits"), prompt: t("chatKit.prompts.feesLimits") },
        { label: t("chatKit.promptLabels.security"), prompt: t("chatKit.prompts.security") },
        { label: t("chatKit.promptLabels.contactSupport"), prompt: t("chatKit.prompts.contactSupport") },
      ],
    },
    composer: {
      placeholder: t("chatKit.composerPlaceholder"),
    },
    onError: ({ error: chatKitError }: { error: Error }) => {
      // Debug logging only in e2e mode
      if (typeof window !== 'undefined' && window.localStorage.getItem('e2e_debug') === 'true') {
        console.error('[PublicChatKitButton] ChatKit error:', chatKitError);
      }
      if (isMountedRef.current) {
        setError(t("chatKit.errors.connectionError"));
      }
    },
  }), [getClientSecret, t]);

  // Initialize ChatKit with memoized config - SDK handles all loading internally
  const chatkit = useChatKit(chatKitConfig);

  // Retry handler - clears cache
  const handleRetry = useCallback(() => {
    setRetryCount(prev => prev + 1);
    setError(null);
    clearTokenCache();

    // Reset by closing and reopening
    if (retryCount < 2) {
      setOpen(false);
      setTimeout(() => setOpen(true), 200);
    } else {
      setError(t("chatKit.errors.unableToConnect"));
    }
  }, [retryCount]);

  // Hide FAB on mobile to prevent overlapping content (Use Cases cards, footer links)
  // Mobile users can access chat via footer link instead
  const floatingButton = !open && !isMobile && (
    <div
      className="fixed right-6 z-[9999]"
      style={{
        bottom: "calc(var(--footer-height, 56px) + var(--safe-area-bottom, 0px) + 1.5rem)",
      }}
    >
      <div className="relative">
        <Button
          size="lg"
          onClick={() => setOpen(true)}
          className={cn(
            // Canonical FAB size: 64×64px (h-16 w-16)
            "h-16 w-16 rounded-full shadow-lg hover:shadow-xl",
            "transition-all duration-240",
            "touch-manipulation",
            "pointer-events-auto cursor-pointer",
            semanticTokens.focus.ring,
            // Canonical FAB style
            "bg-gradient-to-br from-primary to-primary/90",
            "backdrop-blur-sm",
            "hover:from-primary/95 hover:to-primary/85",
            semanticTokens.fg.inverted,
            "border border-white/10",
            // Enhanced shadow with glow effect
            "shadow-lg shadow-primary/25",
            "hover:shadow-xl hover:shadow-primary/30",
          )}
          aria-label={t("chatKit.buttonLabel")}
          aria-describedby="public-assistant-button-description"
          data-testid="public-chatkit-button"
        >
          <LivingLogoIcon
            size={56}
            variant="inverted"
            showEnergyFlow={true}
            showLed={false}
          />
          <span id="public-assistant-button-description" className="sr-only">
            {t("chatKit.buttonDescription")}
          </span>
        </Button>
        {/* LED micro-dot - Outside button contour, bottom right - Enhanced visibility */}
        <LivingLogoLED logoSize={56} className="z-10" show={true} />
      </div>
    </div>
  );

  return (
    <>
      {/* Floating Action Button - hidden when dialog is open to avoid aria-hidden conflict */}
      {floatingButton && (portalNode ? createPortal(floatingButton, portalNode) : floatingButton)}

      {/* ChatKit Dialog - Bottom sheet on mobile (like modern platforms), side panel on desktop */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side={isMobile ? "bottom" : "right"}
          ref={dialogRef}
          className={cn(
            "flex w-full flex-col gap-0 p-0",
            "w-full max-w-full sm:max-w-[420px] lg:w-[32rem] lg:max-w-2xl",
            semanticTokens.bg.surface,
          )}
          style={{
            // Mobile: bottom sheet height (85vh - like internal browser)
            // Desktop: full height side panel
            height: isMobile ? '85dvh' : '100dvh',
            maxHeight: isMobile ? '85dvh' : '100dvh',
          }}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {/* A11y: Hidden title and description for screen readers */}
          <VisuallyHidden>
            <SheetTitle>{t("chatKit.sheetTitle")}</SheetTitle>
            <SheetDescription>{t("chatKit.sheetDescription")}</SheetDescription>
          </VisuallyHidden>

          {/* Header */}
          <div
            className={cn(
              "flex flex-row items-center justify-between border-b",
              "px-4 py-3 sm:px-6 sm:py-4",
              semanticTokens.border.default,
              semanticTokens.bg.surface,
            )}
          >
            <div className="flex items-center gap-3">
              <div className={cn(
                "flex h-10 w-10 items-center justify-center rounded-full",
                "bg-gradient-to-br from-primary to-primary/80"
              )}>
                <LivingLogoIcon
                  size={24}
                  variant={isDarkTheme ? 'dark' : 'inverted'}
                  showEnergyFlow={true}
                  showLed={true}
                />
              </div>
              <div>
                <Heading level={5}>
                  {t("chatKit.title")}
                </Heading>
                <Text variant="caption" color="muted">
                  {t("chatKit.subtitle")}
                </Text>
              </div>
            </div>
          </div>

          {/* Error state */}
          {error && (
            <div className="flex h-full items-center justify-center p-6">
              <div className="text-center space-y-4 max-w-sm">
                <div className={cn(
                  "w-12 h-12 mx-auto rounded-full flex items-center justify-center",
                  semanticTokens.warning.bg
                )}>
                  <LivingLogoIcon size={24} variant="light" showEnergyFlow={false} showLed={false} />
                </div>
                <div>
                  <p className={cn("text-sm font-semibold mb-1", semanticTokens.fg.default)}>
                    {t("chatKit.errors.connectionErrorTitle")}
                  </p>
                  <p className={cn("text-xs", semanticTokens.fg.muted)}>
                    {error}
                  </p>
                </div>
                <Button
                  onClick={handleRetry}
                  size="sm"
                  variant="outline"
                  disabled={retryCount >= 3}
                  className="gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  {retryCount >= 3 ? t("chatKit.actions.contactSupport") : t("chatKit.actions.tryAgain")}
                </Button>
              </div>
            </div>
          )}

          {/* ChatKit widget - SDK handles all loading/error states internally */}
          {!error && (
            <div
              className={cn("flex-1 overflow-hidden", semanticTokens.bg.surface)}
              style={{
                minHeight: 0,
                flex: '1 1 auto',
              }}
            >
              <ChatKit
                control={chatkit.control}
                className="h-full w-full"
              />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
